import { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Button } from 'blaze-ui/Button'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { useModals } from 'almanac-ui/Modal'
import { VisualStylesModal } from 'components/VisualStylesModal'
import Styles from './BrandVisualStylePreview.module.scss'
import { StyleGenerated } from '../BrandVisualStyleSelection/GenerationStage'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Check02 } from 'blaze-ui/icons/16'
import { Images } from 'blaze-ui/icons/24'
import { useStyledImage } from 'components/VisualStylePreviewCarousel/useStyledImage'
import { MediaFile } from 'components/VisualStylePreviewCarousel/VisualStylePreviewCarousel'
import { StyleImageLoader } from '../BrandVisualStyleSelection/StyleImageLoader'
import { useVisualStyles } from 'hooks/queries/visualStyles'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ProgressBar } from 'components/ProgressBar/ProgressBar'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import { TopBar } from '../TopBar/TopBar'
import { useOnboardingStrategiesEnabled } from 'flows/virtualMarketerOnboarding/steps'
import { sampleSize, startCase } from 'lodash'
import { useUpdateBrandVisualKit } from 'hooks/queries/workspaces/useUpdateBrandVisualKit'
import { FontPairing } from 'hooks/queries/fontPairings'
import { useDisplayedFontPairingsAndVisualStyles } from '../BrandVisualStyleSelection/useDisplayedFontPairingsAndVisualStyles'
import { reportError } from 'utils/error'
import { VisualStyle } from 'types'
import { HeadingLevel } from 'blaze-ui/Heading/Heading'

export interface BrandVisualStylePreviewProps {
  onNext: (selectedVisualStyle: StyleGenerated, stylesGenerated: StyleGenerated[], fontPairings?: FontPairing[]) => void
  onBack: () => void
  onProvideBackHandler?: (handler: (() => void) | null) => void
  initialStylesGenerated?: StyleGenerated[]
  initialSelectedStyle?: StyleGenerated
  sourceImage: MediaFile | null
  brandKitId?: number
}

const FAKE_DURATIONS = [65000, 85000, 75000]
const DEFAULT_STYLES = ['Morning Contrast', 'Sunset Studio', 'Black and White Minimal']
const FAKE_PROGRESS_MAX = 95
const MOBILE_FAKE_DURATION = 70000
const LOADING_TIMEOUT_MS = 5 * 60 * 1000
const DESKTOP_WITHOUT_SOURCE_IMAGE_LOADING_TIMEOUT_MS = 30_000

export const BrandVisualStylePreview = ({
  onNext,
  onBack,
  onProvideBackHandler,
  initialStylesGenerated = [],
  initialSelectedStyle,
  sourceImage,
  brandKitId,
}: BrandVisualStylePreviewProps) => {
  const isOnboardingStrategiesEnabled = useOnboardingStrategiesEnabled()
  const { isMobile } = useResponsiveDesign()
  const photoUploadNotAvailable = isOnboardingStrategiesEnabled || isMobile

  const {
    displayedFontPairings,
    displayedVisualStyles: hookDisplayedVisualStyles,
    isLoading: isFontPairingsAndVisualStylesLoading,
  } = useDisplayedFontPairingsAndVisualStyles(photoUploadNotAvailable ? brandKitId : undefined)
  const { openModal } = useModals()
  const { data: visualStyles } = useVisualStyles() as { data: VisualStyle[] }
  const { data: brandKitData } = useBrandKit(brandKitId, { enabled: !!brandKitId })
  const { mutateAsync: updateBrandKit } = useUpdateBrandKit({ id: brandKitId || 0 })
  const { mutateAsync: updateBrandVisualKit } = useUpdateBrandVisualKit({
    id: brandKitData?.brand_kit?.brand_visual_kit?.id ?? 0,
  })

  const [stylesGenerated, setStylesGenerated] = useState<StyleGenerated[]>(sourceImage ? [] : initialStylesGenerated)
  const completedStyleOneRef = useRef<boolean>(false)
  const completedStyleTwoRef = useRef<boolean>(false)
  const completedStyleThreeRef = useRef<boolean>(false)
  const [selectedVisualStyleId, setSelectedVisualStyleId] = useState<number | null>(
    initialSelectedStyle?.id || stylesGenerated[0]?.id,
  )
  const [generationDone, setGenerationDone] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [showMobileLoading, setShowMobileLoading] = useState<boolean>(isMobile)
  const [mobileProgress, setMobileProgress] = useState<number>(0)
  const [showMobileSeeAll, setShowMobileSeeAll] = useState<boolean>(false)
  const [seeAllSelectedId, setSeeAllSelectedId] = useState<number | null>(null)

  const fakeProgressAnimationRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const hasCompletedMobileLoadingRef = useRef(false)
  const hasSetGenerationDoneRef = useRef(false)

  const displayedVisualStyles = useMemo(() => {
    if (isOnboardingStrategiesEnabled) {
      if (hookDisplayedVisualStyles.length > 0) {
        if (!selectedVisualStyleId) {
          setSelectedVisualStyleId(hookDisplayedVisualStyles[0]?.id)
          setShowMobileLoading(false)
        }

        return hookDisplayedVisualStyles.slice(0, 3).map((style) => ({
          ...style,
          styleUrl: style.cover_image_url,
        }))
      }

      return []
    }
    if (initialStylesGenerated.length > 0) {
      return initialStylesGenerated.slice(0, 3)
    }
    return visualStyles?.slice(0, 3) || []
  }, [
    isOnboardingStrategiesEnabled,
    hookDisplayedVisualStyles,
    initialStylesGenerated,
    visualStyles,
    selectedVisualStyleId,
  ])

  const showLoadingStylesForStrategies = isOnboardingStrategiesEnabled && !displayedVisualStyles.length
  const loadingStyles = useMemo(
    () =>
      showLoadingStylesForStrategies || !displayedVisualStyles.length
        ? sampleSize(visualStyles || [], 3)
        : displayedVisualStyles.slice(0, 3),
    [showLoadingStylesForStrategies, displayedVisualStyles, visualStyles],
  )

  const allComplete = useMemo(() => {
    return completedStyleOneRef.current && completedStyleTwoRef.current && completedStyleThreeRef.current
  }, [completedStyleOneRef, completedStyleTwoRef, completedStyleThreeRef])

  const styled1 = useStyledImage(sourceImage || undefined, displayedVisualStyles[0]?.id ?? null, brandKitId)
  const styled2 = useStyledImage(sourceImage || undefined, displayedVisualStyles[1]?.id ?? null, brandKitId)
  const styled3 = useStyledImage(sourceImage || undefined, displayedVisualStyles[2]?.id ?? null, brandKitId)
  const styledImages = useMemo(() => [styled1, styled2, styled3], [styled1, styled2, styled3])

  const stylesFinishedGenerating =
    styledImages.every((s) => s.styledUrl) || generationDone || isOnboardingStrategiesEnabled

  const inlineDisplayStyles = useMemo(() => {
    const isLoading = (!!sourceImage && !stylesFinishedGenerating) || showLoadingStylesForStrategies
    if (isLoading) return loadingStyles
    if (stylesGenerated.length >= 3) return stylesGenerated
    return displayedVisualStyles
  }, [
    sourceImage,
    stylesFinishedGenerating,
    showLoadingStylesForStrategies,
    loadingStyles,
    stylesGenerated,
    displayedVisualStyles,
  ])

  const markComplete = (index: number) => {
    if (index === 0) {
      completedStyleOneRef.current = true
    } else if (index === 1) {
      completedStyleTwoRef.current = true
    } else if (index === 2) {
      completedStyleThreeRef.current = true
    }
  }

  const anyHasFailed = styledImages.every((s) => s.hasFailed)

  useEffect(() => {
    if (!visualStyles?.length || (isMobile && !showMobileLoading)) return

    const forceComplete = (errorMessage: string) => {
      if (hasCompletedMobileLoadingRef.current) return
      hasCompletedMobileLoadingRef.current = true

      if (fakeProgressAnimationRef.current !== null) {
        clearInterval(fakeProgressAnimationRef.current)
        fakeProgressAnimationRef.current = null
      }

      reportError(new Error(errorMessage), { metaData: { brandKitId } }, { context: 'mobile_visual_style_generation' })

      const styles = displayedVisualStyles.length > 0 ? displayedVisualStyles : visualStyles?.slice(0, 3) || []
      if (!hasSetGenerationDoneRef.current && styles.length > 0) {
        hasSetGenerationDoneRef.current = true
        setGenerationDone(true)
        setStylesGenerated(
          styles.map((style, index) => ({
            ...style,
            styleUrl: styledImages[index]?.styledUrl || style.cover_image_url || '',
          })),
        )
        setSelectedVisualStyleId(styles[0]?.id)
      }

      setShowMobileLoading(false)
    }

    if (anyHasFailed) {
      forceComplete('Visual style generation failed after retries')
      return
    }

    const timer = setTimeout(
      () => forceComplete('Visual style generation timed out'),
      isOnboardingStrategiesEnabled ? DESKTOP_WITHOUT_SOURCE_IMAGE_LOADING_TIMEOUT_MS : LOADING_TIMEOUT_MS,
    )

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, showMobileLoading, sourceImage, anyHasFailed, visualStyles?.length])

  useEffect(() => {
    if (
      !sourceImage ||
      !stylesFinishedGenerating ||
      displayedVisualStyles.length !== 3 ||
      hasSetGenerationDoneRef.current
    )
      return

    hasSetGenerationDoneRef.current = true
    setGenerationDone(true)

    const generatedStyles =
      displayedVisualStyles.length > 0
        ? displayedVisualStyles.map((style, index) => ({
            ...style,
            styleUrl: styledImages[index].styledUrl || '',
          }))
        : initialStylesGenerated
    setStylesGenerated(generatedStyles)

    const preservedSelection =
      initialSelectedStyle?.id && generatedStyles.some((s) => s.id === initialSelectedStyle.id)
        ? initialSelectedStyle.id
        : generatedStyles[0]?.id ?? null

    setSelectedVisualStyleId(preservedSelection)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sourceImage,
    allComplete,
    completedStyleOneRef.current,
    completedStyleTwoRef.current,
    completedStyleThreeRef.current,
    displayedVisualStyles,
    styledImages,
    isOnboardingStrategiesEnabled,
    initialSelectedStyle,
    initialStylesGenerated,
    stylesFinishedGenerating,
  ])

  useEffect(() => {
    if (stylesGenerated.length || sourceImage || !visualStyles || !!isOnboardingStrategiesEnabled) return

    let defaultStyles = DEFAULT_STYLES.map((name) => visualStyles.find((style) => style.name === name)).filter(
      (style): style is NonNullable<typeof style> => style !== undefined,
    )

    if (!defaultStyles.length) {
      defaultStyles = visualStyles.slice(0, 3)
    }

    if (defaultStyles.length === 3) {
      setSelectedVisualStyleId(defaultStyles[0].id)
      setStylesGenerated(
        defaultStyles.map((style) => ({
          ...style,
          styleUrl: style.cover_image_url,
          metadata: style.metadata,
        })),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceImage, visualStyles])

  useEffect(() => {
    if (!sourceImage) return
    hasSetGenerationDoneRef.current = false
    completedStyleOneRef.current = false
    completedStyleTwoRef.current = false
    completedStyleThreeRef.current = false
    setGenerationDone(false)
    if (isMobile) {
      setShowMobileLoading(true)
      setMobileProgress(0)
      hasCompletedMobileLoadingRef.current = false
    }
  }, [sourceImage, isMobile])

  useEffect(() => {
    if (!isMobile || !showMobileLoading) return

    const startTime = Date.now()

    const tick = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min((elapsed / MOBILE_FAKE_DURATION) * FAKE_PROGRESS_MAX, FAKE_PROGRESS_MAX)
      setMobileProgress(Math.round(progress))

      if (progress >= FAKE_PROGRESS_MAX && fakeProgressAnimationRef.current !== null) {
        clearInterval(fakeProgressAnimationRef.current)
        fakeProgressAnimationRef.current = null
      }
    }

    fakeProgressAnimationRef.current = setInterval(tick, 250)

    return () => {
      if (fakeProgressAnimationRef.current !== null) {
        clearInterval(fakeProgressAnimationRef.current)
        fakeProgressAnimationRef.current = null
      }
    }
  }, [isMobile, showMobileLoading])

  useEffect(() => {
    if (
      !isMobile ||
      !showMobileLoading ||
      (!allComplete && !isOnboardingStrategiesEnabled) ||
      hasCompletedMobileLoadingRef.current
    )
      return

    hasCompletedMobileLoadingRef.current = true

    if (fakeProgressAnimationRef.current !== null) {
      clearInterval(fakeProgressAnimationRef.current)
      fakeProgressAnimationRef.current = null
    }

    const startProgress = mobileProgress
    const startTime = Date.now()
    const duration = 400
    let cancelled = false

    const tick = () => {
      if (cancelled) return
      const elapsed = Date.now() - startTime
      const progress = Math.min(startProgress + (100 - startProgress) * (elapsed / duration), 100)

      if (progress >= 100) {
        setMobileProgress(100)
        setTimeout(() => {
          setShowMobileLoading(false)
        }, 500)
      } else {
        setMobileProgress(Math.round(progress))
        setTimeout(tick, 30)
      }
    }

    setTimeout(tick, 30)

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, showMobileLoading, allComplete])

  const handleBack = useCallback(() => {
    if (showMobileSeeAll) {
      setShowMobileSeeAll(false)
    } else {
      onBack()
    }
  }, [showMobileSeeAll, onBack])

  useEffect(() => {
    if (!onProvideBackHandler || !isMobile) return
    onProvideBackHandler(handleBack)
  }, [handleBack, onProvideBackHandler, isMobile])

  const handleStyleSelection = (styleId: number | null) => {
    if (!styleId) return

    const existingIndex = stylesGenerated.findIndex((s) => s.id === styleId)

    if (existingIndex !== -1) {
      if (existingIndex > 2) {
        setStylesGenerated([
          stylesGenerated[0],
          stylesGenerated[existingIndex],
          ...stylesGenerated.slice(1, existingIndex),
          ...stylesGenerated.slice(existingIndex + 1),
        ])
      }
      setSelectedVisualStyleId(styleId)
      return
    }

    const newStyleToAdd = visualStyles?.find((s) => s.id === styleId)
    if (newStyleToAdd) {
      const newStyle: StyleGenerated = {
        id: newStyleToAdd.id,
        name: newStyleToAdd.name,
        cover_image_url: newStyleToAdd.cover_image_url,
        styleUrl: newStyleToAdd.cover_image_url,
        metadata: newStyleToAdd.metadata,
      }
      let baseStyles: (VisualStyle | StyleGenerated)[]
      if (stylesGenerated.length >= 3) {
        baseStyles = stylesGenerated
      } else if (isOnboardingStrategiesEnabled) {
        baseStyles = displayedVisualStyles
      } else {
        baseStyles = visualStyles?.slice(0, 3) || []
      }
      const toStyleGenerated = (s: VisualStyle | StyleGenerated): StyleGenerated =>
        'styleUrl' in s ? (s as StyleGenerated) : { ...s, styleUrl: (s as VisualStyle).cover_image_url }
      const fallbacks = (visualStyles || []).filter((s) => s.id !== styleId)
      const leftSource = baseStyles[0] ?? fallbacks[0]
      const rightSource = baseStyles[2] ?? fallbacks[1]
      const left = leftSource ? toStyleGenerated(leftSource) : newStyle
      const right = rightSource ? toStyleGenerated(rightSource) : newStyle
      setStylesGenerated([left, newStyle, right])
      setSelectedVisualStyleId(styleId)
    }
  }

  const handleNext = async () => {
    if (!selectedVisualStyleId || isSaving) return

    const selectedVisualStyle = displayedVisualStyles.find((s) => s.id === selectedVisualStyleId)
    const selectedVisualStyleGenerated = {
      ...selectedVisualStyle,
      id: selectedVisualStyleId,
      cover_image_url: selectedVisualStyle?.cover_image_url || '',
      styleUrl: selectedVisualStyle?.cover_image_url || '',
      name: '',
    }
    const selectedStyle = stylesGenerated.find((s) => s.id === selectedVisualStyleId) ||
      selectedVisualStyleGenerated || {
        id: selectedVisualStyleId,
        styleUrl: '',
        name: '',
        cover_image_url: '',
        metadata: {
          color: '',
          contrast: '',
          display_description: '',
          focus: '',
          grain: '',
          lighting: '',
        },
      }

    if (brandKitId) {
      setIsSaving(true)
      try {
        await Promise.all([
          updateBrandKit({
            content_preferences: {
              ...brandKitData?.brand_kit?.content_preferences,
              apply_visual_style: true,
              image_media_priority: 'only_my_brand_kit',
            },
          }),
          updateBrandVisualKit({ visual_style_id: selectedVisualStyleId }),
        ])
      } finally {
        setIsSaving(false)
      }
    }

    onNext(selectedStyle, stylesGenerated, photoUploadNotAvailable ? displayedFontPairings : undefined)
  }

  const isButtonDisabled =
    (!!sourceImage && !stylesFinishedGenerating) ||
    isSaving ||
    isFontPairingsAndVisualStylesLoading ||
    !selectedVisualStyleId

  const getMobileLoadingStage = () => {
    if (mobileProgress < 30) return 'Analyzing source image...'
    if (mobileProgress < 60) return 'Generating styles...'
    if (mobileProgress < 90) return 'Applying transformations...'
    return 'Finalizing...'
  }

  const mobileLoaders = isMobile && sourceImage && (
    <div style={{ position: 'fixed', top: -9999, left: -9999, pointerEvents: 'none' }}>
      {displayedVisualStyles.map((style, index) => (
        <StyleImageLoader
          key={`${sourceImage.id}-${style.id}`}
          sourceImage={sourceImage}
          stylesGeneration={styledImages[index]}
          onTransformedImageLoaded={() => markComplete(index)}
          withTag={false}
          fakeDuration={FAKE_DURATIONS[index]}
        />
      ))}
    </div>
  )

  if (isMobile && showMobileLoading && (sourceImage || isOnboardingStrategiesEnabled)) {
    return (
      <>
        <div className={Styles.mobileLoadingScreen} data-visual-style-page="true">
          <div className={Styles.mobileTopBar} />
          <div className={Styles.mobileLoadingBackground}>
            <img src={sourceImage?.url} alt="" className={Styles.mobileLoadingBackgroundImage} />
            <div className={Styles.mobileLoadingGradient} />
          </div>
          <div className={Styles.mobileLoadingContent}>
            <div className={Styles.mobileLoadingProgressContainer}>
              <ProgressBar
                percentage={mobileProgress}
                color="var(--light-100)"
                backgroundColor="var(--light-60)"
                size="xs"
                width="140px"
              />
            </div>
            <Text variant="secondary" color="var(--light-60)">
              {isOnboardingStrategiesEnabled ? 'Loading style' : getMobileLoadingStage()}
            </Text>
            <Text variant="secondary" color="var(--light-60)">
              {mobileProgress}%
            </Text>
          </div>
        </div>
        {mobileLoaders}
      </>
    )
  }

  if (isMobile && showMobileSeeAll) {
    const seeAllSelectedStyle = visualStyles?.find((s) => s.id === seeAllSelectedId)

    const handleSeeAllConfirm = () => {
      if (seeAllSelectedId !== null) {
        handleStyleSelection(seeAllSelectedId)
        setShowMobileSeeAll(false)
      }
    }

    return (
      <div className={Styles.mobileSeeAllScreen} data-visual-style-page="true">
        <div className={Styles.mobileSeeAllContent}>
          <div className={Styles.mobileSeeAllGrid}>
            {visualStyles?.map((style) => (
              <div
                key={style.id}
                className={cx(Styles.mobileSeeAllCard, {
                  [Styles.selected]: seeAllSelectedId === style.id,
                })}
                onClick={() => setSeeAllSelectedId(style.id)}
              >
                <img src={style.cover_image_url} alt={style.name} />
              </div>
            ))}
          </div>
        </div>

        <div className={Styles.mobileSeeAllBottomCta}>
          <div className={Styles.mobileSeeAllStyleName}>
            <Heading level={2} color="var(--light-100)">
              {seeAllSelectedStyle?.name || ''}
            </Heading>
          </div>
          <Button
            variant="secondary"
            size="lg"
            onPress={handleSeeAllConfirm}
            isDisabled={seeAllSelectedId === null}
            className={Styles.mobileSeeAllUseButton}
          >
            Use this style
          </Button>
        </div>
      </div>
    )
  }

  const showMobilePreview =
    isMobile && ((sourceImage && !showMobileLoading && stylesFinishedGenerating) || !sourceImage)

  if (showMobilePreview) {
    let selectedStyle
    let selectedStyleUrl

    if (isOnboardingStrategiesEnabled) {
      selectedStyle = visualStyles?.find((s) => s.id === selectedVisualStyleId)
      selectedStyleUrl = selectedStyle?.cover_image_url
    } else {
      selectedStyle = stylesGenerated.find((s) => s.id === selectedVisualStyleId)
      selectedStyleUrl = selectedStyle?.styleUrl || selectedStyle?.cover_image_url
    }

    return (
      <div className={Styles.mobilePreviewContainer} data-visual-style-page="true">
        {isOnboardingStrategiesEnabled && (
          <div className={Styles.mobileTopBar}>
            <Text color="var(--light-100)">Choose a Visual Style</Text>
          </div>
        )}
        <div className={Styles.mobileHeroSection}>
          {selectedStyleUrl && <img src={selectedStyleUrl} alt="" className={Styles.mobileHeroImage} />}
          <div className={Styles.mobileHeroGradient} />
        </div>

        <div className={Styles.mobileThumbnailsSection}>
          <div className={Styles.mobileStyleName}>
            <Heading level={2} color="var(--light-100)">
              {selectedStyle?.name}
            </Heading>
          </div>
          <div className={Styles.mobileThumbnailsContainer}>
            {inlineDisplayStyles.slice(0, 3).map((style) => {
              const styleUrl = (style as StyleGenerated).styleUrl || (style as VisualStyle).cover_image_url

              return (
                <div
                  key={style.id}
                  className={cx(Styles.mobileThumbnail, {
                    [Styles.selected]: selectedVisualStyleId === style.id,
                  })}
                  onClick={() => setSelectedVisualStyleId(style.id)}
                >
                  <img src={styleUrl} alt={style.name} />
                </div>
              )
            })}
            <button
              className={Styles.mobileSeeAllButton}
              onClick={() => {
                setSeeAllSelectedId(selectedVisualStyleId)
                setShowMobileSeeAll(true)
              }}
            >
              <Images size={24} color="var(--light-100)" />
              <Text variant="label" color="var(--light-100)">
                See All
              </Text>
            </button>
          </div>

          <Button
            variant="secondary"
            size="lg"
            onPress={handleNext}
            isDisabled={isButtonDisabled}
            className={Styles.mobileUseStyleButton}
          >
            {isSaving ? 'Saving...' : 'Use this style'}
          </Button>
        </div>
      </div>
    )
  }

  let headingLevel = window.innerWidth < 1100 ? 2 : 1
  if (window.innerWidth < 950) headingLevel = 3
  const metadataItemVariant = window.innerWidth < 1100 ? 'label' : 'secondary'

  return (
    <>
      <MultiStepStepLayout
        title="Here's your recommended visual styles"
        description="Applied to all your content automatically. Change it anytime."
        scrollContainerClassName={Styles.scrollContainer}
        contentClassName={Styles.customContent}
      >
        <StickyFooterProvider
          footer={
            <StickyFooter>
              <FooterButtons>
                <div className={Styles.footerSide}>
                  <Button variant="tertiary" size="lg" onPress={onBack}>
                    Back
                  </Button>
                </div>
                <div className={Styles.footerCenter}>
                  <Button
                    size="lg"
                    variant="secondary"
                    onPress={() =>
                      openModal(VisualStylesModal, {
                        selectedStyleId: selectedVisualStyleId,
                        onConfirm: handleStyleSelection,
                      })
                    }
                    isDisabled={isButtonDisabled}
                    color="var(--dark-80)"
                  >
                    Browse all styles
                  </Button>
                </div>
                <div className={Styles.footerSide}>
                  <Button size="lg" onPress={handleNext} isDisabled={isButtonDisabled}>
                    {isSaving ? 'Saving...' : 'Apply this style'}
                  </Button>
                </div>
              </FooterButtons>
            </StickyFooter>
          }
        >
          <TopBar highlightStep="yourBrand" />
          <div className={Styles.stylesContainer}>
            {(sourceImage && !stylesFinishedGenerating) || showLoadingStylesForStrategies
              ? loadingStyles?.map((style, index) => (
                  <div className={Styles.styleContainer} key={style.id} style={{ cursor: 'default' }}>
                    <StyleImageLoader
                      sourceImage={
                        sourceImage || ({ url: style.cover_image_url, type: 'image', id: style.id } as MediaFile)
                      }
                      stylesGeneration={styledImages[index]}
                      onTransformedImageLoaded={() => markComplete(index)}
                      withTag={false}
                      fakeDuration={FAKE_DURATIONS[index]}
                      loadingMessageOverride={!sourceImage ? 'Loading style' : undefined}
                    />
                  </div>
                ))
              : inlineDisplayStyles.slice(0, 3).map((style, index) => {
                  const useStaticImage = !sourceImage || style.id !== displayedVisualStyles[index]?.id
                  return (
                    <div
                      className={cx(Styles.styleContainer, { [Styles.isSelected]: selectedVisualStyleId === style.id })}
                      key={style.id}
                      onClick={() => setSelectedVisualStyleId(style.id)}
                    >
                      <StyleImageLoader
                        sourceImage={
                          sourceImage || ({ url: style.cover_image_url, type: 'image', id: style.id } as MediaFile)
                        }
                        stylesGeneration={styledImages[index]}
                        staticMode={useStaticImage}
                        staticImageUrl={style.cover_image_url}
                        withTag={false}
                      />

                      <div className={Styles.styleNameContainer}>
                        <div className={Styles.styleData}>
                          <Heading color="var(--light-100)" level={headingLevel as HeadingLevel}>
                            {style.name}
                          </Heading>
                          <div className={Styles.styleMetadata}>
                            <div className={Styles.styleMetadataRow}>
                              <div className={Styles.styleMetadataItem}>
                                <span className={Styles.styleMetadataItemLabel}>Lighting</span>
                                <Text variant={metadataItemVariant} color="var(--light-100)">
                                  {startCase(style.metadata?.lighting)}
                                </Text>
                              </div>
                              <div className={Styles.styleMetadataItem}>
                                <span className={Styles.styleMetadataItemLabel}>Color</span>
                                <Text variant={metadataItemVariant} color="var(--light-100)">
                                  {startCase(style.metadata?.color)}
                                </Text>
                              </div>
                            </div>
                            <div className={Styles.styleMetadataRow}>
                              <div className={Styles.styleMetadataItem}>
                                <span className={Styles.styleMetadataItemLabel}>Contrast</span>
                                <Text variant={metadataItemVariant} color="var(--light-100)">
                                  {startCase(style.metadata?.contrast)}
                                </Text>
                              </div>
                              <div className={Styles.styleMetadataItem}>
                                <span className={Styles.styleMetadataItemLabel}>Grain</span>
                                <Text variant={metadataItemVariant} color="var(--light-100)">
                                  {startCase(style.metadata?.grain)}
                                </Text>
                              </div>
                              <div className={Styles.styleMetadataItem}>
                                <span className={Styles.styleMetadataItemLabel}>Focus</span>
                                <Text variant={metadataItemVariant} color="var(--light-100)">
                                  {startCase(style.metadata?.focus)}
                                </Text>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {index === 0 && <div className={Styles.recommendedTag}>Recommended</div>}
                      {selectedVisualStyleId === style.id ? (
                        <div className={Styles.checkmark}>
                          <Check02 size={20} color="var(--dark-80)" />
                        </div>
                      ) : (
                        <div className={Styles.emptyCheckmark} />
                      )}
                    </div>
                  )
                })}
          </div>
        </StickyFooterProvider>
      </MultiStepStepLayout>
    </>
  )
}
